import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { readonly, ref, type Ref } from 'vue'
import {
    LittaApiError,
    littaApiFetchJson,
    waitForAuthUser,
} from '@/lib/api/litta-api-fetch'

/** Role keys held per `litta.applications.key`. */
export type RolesByApplication = Record<string, string[]>

export interface Me {
    email: string | null
    name: string | null
    roles: RolesByApplication
    userId: string | null
}

/**
 * Signs the caller in to the platform and returns what they may do, everywhere.
 *
 * A POST because it writes: a first sign-in provisions the `users` row and
 * records that this account has signed in to Beehive.
 */
export async function fetchMe(): Promise<Me> {
    return littaApiFetchJson<Me>('/auth/me', {
        method: 'POST',
        body: JSON.stringify({ application: 'beehive' }),
    })
}

/** Who Firebase currently says is signed in; null between sign-out and sign-in. */
export function currentUid(): string | null {
    return getAuth().currentUser?.uid ?? null
}

const uidRef = ref<string | null>(null)
let observing = false

export function useCurrentUid(): Readonly<Ref<string | null>> {
    if (!observing) {
        observing = true
        uidRef.value = currentUid()
        onAuthStateChanged(getAuth(), user => {
            uidRef.value = user?.uid ?? null
        })
    }
    return readonly(uidRef)
}

export const ME_TTL_MS = 60_000
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000]
export const RETRY_AFTER_FAILURE_MS = 10_000

interface CachedMe {
    expiresAt: number
    promise: Promise<Me>
    uid: string | null
}

let cached: CachedMe | null = null

let lastKnown: { me: Me; uid: string | null } | null = null
let failingFor: { uid: string | null } | null = null

export function resetMe(): void {
    cached = null
    lastKnown = null
    failingFor = null
}

export class IdentityChangedError extends Error {
    constructor() {
        super('The signed-in user changed while their roles were loading')
        this.name = 'IdentityChangedError'
    }
}

export const isTransientError = (error: unknown): boolean =>
    !(error instanceof IdentityChangedError) &&
    (!(error instanceof LittaApiError) ||
        error.status === 429 ||
        error.status >= 500)

export const isRetryingRoles = (): boolean => failingFor?.uid === currentUid()

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

async function fetchMeAs(uid: string | null): Promise<Me> {
    if (currentUid() !== uid) throw new IdentityChangedError()
    const me = await fetchMe()
    if (currentUid() !== uid) throw new IdentityChangedError()
    return me
}

async function fetchMeWithRetry(uid: string | null): Promise<Me> {
    for (const delay of [...RETRY_DELAYS_MS, null]) {
        try {
            return await fetchMeAs(uid)
        } catch (error) {
            if (delay === null || !isTransientError(error)) throw error
            await wait(delay)
        }
    }
    throw new Error('unreachable')
}

async function loadMe(uid: string | null): Promise<{ fresh: boolean; me: Me }> {
    const known = lastKnown?.uid === uid ? lastKnown.me : null
    try {
        const me = await (known ? fetchMeAs(uid) : fetchMeWithRetry(uid))
        lastKnown = { me, uid }
        failingFor = null
        return { fresh: true, me }
    } catch (error) {
        if (!known || !isTransientError(error)) throw error
        if (currentUid() !== uid) throw new IdentityChangedError()
        console.warn(
            'Could not refresh roles from POST /auth/me, keeping the ones already loaded.',
            error,
        )
        failingFor = { uid }
        return { fresh: false, me: known }
    }
}

/**
 * Shared with the router guard, which runs before any component mounts and so
 * cannot use the vue-query hook. One in-flight request regardless of how many
 * navigations happen while it resolves. Keyed by uid, so signing out and in
 * as somebody else does not inherit the first person's roles, and expiring,
 * so a refetch is a real one rather than the same promise handed back.
 */
export async function fetchMeCached(): Promise<Me> {
    /* Awaited rather than read, so a deep link during Firebase's startup is
     * cached under the person it resolves to, not under "nobody yet". */
    const uid = (await waitForAuthUser())?.uid ?? null
    const now = Date.now()
    if (cached?.uid !== uid || cached.expiresAt <= now) {
        let entry: CachedMe
        const promise = loadMe(uid).then(
            ({ fresh, me }) => {
                if (!fresh)
                    entry.expiresAt = Date.now() + RETRY_AFTER_FAILURE_MS / 2
                return me
            },
            (error: unknown) => {
                if (cached === entry) cached = null
                console.warn(
                    'Could not read roles from POST /auth/me, so every role-gated area will be hidden.',
                    error,
                )
                throw error
            },
        )
        entry = { expiresAt: now + ME_TTL_MS, promise, uid }
        cached = entry
    }
    return cached.promise
}
